import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { ChevronLeft, Close, ExpandMore, ZoomInMap } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import measurement_empty_state from "shared/assets/measurementEmptyState.svg";
import UseAuth from "shared/common/hooks/UseAuth";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import measurementTools, {
  MeasurementType,
} from "shared/common/utils/measurements/measurementTools";

export default function MeasurementContent() {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const loading = defaultStore((state) => state.loading);
  const activeCampaign = defaultStore((state) => state.activeCampaign);
  const manager = defaultStore((state) => state.manager);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const clientConfig = defaultStore((state) => state.clientConfig);
  const axisCollection = defaultStore((state) => state.axisCollection);

  const measurementActiveTool = sidebarStore(
    (state) => state.measurementActiveTool
  );
  const changeMeasurementActiveTool = sidebarStore(
    (state) => state.changeMeasurementActiveTool
  );
  const lastMeasurementActiveTool = sidebarStore(
    (state) => state.lastMeasurementActiveTool
  );
  const changeLastMeasurementActiveTool = sidebarStore(
    (state) => state.changeLastMeasurementActiveTool
  );
  const measurementPopover = sidebarStore((state) => state.measurementPopover);
  const showDescription = sidebarStore((state) => state.showDescription);
  const changeShowDescription = sidebarStore(
    (state) => state.changeShowDescription
  );
  const changeShowSmallToolContent = sidebarStore(
    (state) => state.changeShowSmallToolContent
  );
  const changeSmallToolContent = sidebarStore(
    (state) => state.changeSmallToolContent
  );
  const maximizeTable = sidebarStore((state) => state.maximizeTable);
  const changeMaximizeTable = sidebarStore(
    (state) => state.changeMaximizeTable
  );
  const changeVerticalDistanceToolValues = sidebarStore(
    (state) => state.changeVerticalDistanceToolValues
  );
  const orthogonalDistanceTool = sidebarStore(
    (state) => state.orthogonalDistanceTool
  );
  const changeOrthogonalDistanceTool = sidebarStore(
    (state) => state.changeOrthogonalDistanceTool
  );
  const changeOrthogonalDistanceToolValues = sidebarStore(
    (state) => state.changeOrthogonalDistanceToolValues
  );
  const orthogonalDistanceActiveAttributes = sidebarStore(
    (state) => state.orthogonalDistanceActiveAttributes
  );
  const changeDistanceToolValues = sidebarStore(
    (state) => state.changeDistanceToolValues
  );
  const changeAreaToolValues = sidebarStore(
    (state) => state.changeAreaToolValues
  );
  const changeCoordinateToolValues = sidebarStore(
    (state) => state.changeCoordinateToolValues
  );
  const changeProfileToolValues = sidebarStore(
    (state) => state.changeProfileToolValues
  );
  const changeLinrefToolValues = sidebarStore(
    (state) => state.changeLinrefToolValues
  );
  const distanceActiveAttributes = sidebarStore(
    (state) => state.distanceActiveAttributes
  );
  const verticalDistanceActiveAttributes = sidebarStore(
    (state) => state.verticalDistanceActiveAttributes
  );
  const areaActiveAttributes = sidebarStore(
    (state) => state.areaActiveAttributes
  );
  const coordinatesActiveAttributes = sidebarStore(
    (state) => state.coordinatesActiveAttributes
  );
  const surveyWheelActiveAttributes = sidebarStore(
    (state) => state.surveyWheelActiveAttributes
  );
  const linrefActiveAttributes = sidebarStore(
    (state) => state.linrefActiveAttributes
  );
  const distanceTool = sidebarStore((state) => state.distanceTool);
  const changeDistanceTool = sidebarStore((state) => state.changeDistanceTool);
  const profileTool = sidebarStore((state) => state.profileTool);
  const linrefTool = sidebarStore((state) => state.linrefTool);
  const changeProfileTool = sidebarStore((state) => state.changeProfileTool);
  const changeLinrefTool = sidebarStore((state) => state.changeLinrefTool);
  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const surveyWheelTool = sidebarStore((state) => state.surveyWheelTool);
  const changeSurveyWheelTool = sidebarStore(
    (state) => state.changeSurveyWheelTool
  );
  const changeSurveyWheelToolValues = sidebarStore(
    (state) => state.changeSurveyWheelToolValues
  );

  const { userData } = UseAuth();

  const [activeTool, setActiveTool] = useState<MeasurementType>();
  const [canShowSmallWindow, setCanShowSmallWindow] = useState<boolean>(false);

  const [canMeasure, setCanMeasure] = useState<boolean>(false);

  const [verticalDistanceTool, setVerticalDistanceTool] =
    useState<mm.ElevationTool>();
  const [areaTool, setAreaTool] = useState<mm.DrawPolygonTool>();
  const [coordinateTool, setCoordinateTool] = useState<mm.DrawPointTool>();

  const tools: MeasurementType[] = measurementTools();

  //close measurement popover
  const handleCloseMeasurement = () => {
    closeAllToolPopover();
  };

  //activate selected measure tool
  const activateTool = (tool: MeasurementType) => {
    if (activeTool?.id == tool.id) {
      setActiveTool(undefined);
      changeMeasurementActiveTool([]);
      changeLastMeasurementActiveTool([]);
      return;
    }
    setActiveTool(tool);
    changeMeasurementActiveTool([tool]);
    changeLastMeasurementActiveTool([tool]);
  };

  //show or hide description
  const handleDescription = () => {
    changeShowDescription(!showDescription);
  };

  //show small tool content
  const handleToolSmallWindow = () => {
    handleCloseMeasurement();
    changeShowSmallToolContent(true);
  };

  //change small tool content based on active measurement tool
  useEffect(() => {
    if (measurementActiveTool.length > 0) {
      changeSmallToolContent(measurementActiveTool[0]);
    } else {
      changeSmallToolContent(undefined);
    }
    enableOrDisableTools(measurementActiveTool);
  }, [measurementActiveTool]);

  //check if should open or not small tool
  useEffect(() => {
    checkSmallWindow(measurementActiveTool);
  }, [
    measurementActiveTool,
    areaActiveAttributes,
    distanceActiveAttributes,
    verticalDistanceActiveAttributes,
    coordinatesActiveAttributes,
    linrefActiveAttributes,
  ]);

  //update active tool everytime language is changed
  useEffect(() => {
    if (tools && activeTool) {
      setActiveTool(tools.find((item) => item.id === activeTool.id));
    }
  }, [t]);

  //check if should open or not small tool
  const checkSmallWindow = (tools: MeasurementType[]) => {
    const canHaveSmallWindow = tools.find(
      (tool: MeasurementType) => tool.id !== "profile"
    );
    if (tools.length > 0 && canHaveSmallWindow !== undefined) {
      switch (canHaveSmallWindow.id) {
        case "measureArea":
          if (areaActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "distance":
          if (distanceActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "surveyWheel":
          if (surveyWheelActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "verticalDistance":
          if (verticalDistanceActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "orthogonalDistance":
          if (orthogonalDistanceActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "coordinate":
          if (coordinatesActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "linref":
          if (linrefActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        default:
          setCanShowSmallWindow(false);
      }
    } else {
      setCanShowSmallWindow(false);
    }
  };

  //save measure distance values on finished
  const onFinished = (info: any) => {
    changeDistanceToolValues(info);
  };
  const onContinousDraw = (info: any) => {
    changeDistanceToolValues(info);
  };

  const distOptions = {
    finishedcbf: onFinished,
    resetcbf: () => changeDistanceToolValues(undefined),
    continousdrawcbf: onContinousDraw,
  };

  const surveyWheelOptions = {
    pointsetcbf: (_: PointerEvent, len: number) => {
      changeSurveyWheelToolValues(len);
    },
    resetcbf: () => changeSurveyWheelTool(undefined),
    continousdrawcbf: (value: number) => {
      if (value !== null) changeSurveyWheelToolValues(value);
    },
    axislayerstyle: new mm.FeatureStyle({
      stylename: "tool_draw",
      rules: [
        {
          filter: "",
          symbolizer: "point",
          params: {
            "font-marker": {
              name: "circle-o",
            },
            size: 10,
            stroke: [1, 0.4, 0, 0.8],
          },
        },
        {
          filter: "",
          symbolizer: "line",
          params: {
            "stroke-width": 1.5,
            stroke: [1, 0.4, 0, 0.8],
            "stroke-dash-width": 4,
            "stroke-gap-width": 2,
          },
        },
      ],
    }),

    highlightstyle: new mm.FeatureStyle({
      stylename: "highlight",
      rules: [
        {
          filter: "name==precise",
          symbolizer: "line",
          params: {
            "stroke-width": 5,
            stroke: [1, 0.4, 0, 1],
          },
        },
        {
          filter: "name==predict",
          symbolizer: "line",
          params: {
            "stroke-width": 5,
            stroke: [1, 0.4, 0, 1],
            "stroke-dash-width": 3,
            "stroke-gap-width": 2,
          },
        },
        {
          filter: "",
          symbolizer: "point",
          params: {
            "font-marker": {
              name: "circle-o",
            },
            size: 10,
            stroke: [1, 0.4, 0, 1],
          },
        },
      ],
    }),
  };

  //save measure vertical values on finished
  const resetProfile = () => {
    changeProfileToolValues(undefined);
  };
  const displayHeight = (info: any) => {
    changeVerticalDistanceToolValues(info);
  };
  const error = (msg: string) => {
    if (msg === "tool_reset_key_changed")
      changeAlertMessage({
        message: t(`alert_info.tool_reset_key_changed`),
        type: "info",
      });
  };
  const verticalDistOptions = {
    finishedcbf: displayHeight,
    resetcbf: () => changeVerticalDistanceToolValues(undefined),
    errorcbf: error,
    continousdraw: true,
    maxdist: 30,
  };

  //save measure area values on finished
  const onFinishedArea = (info: any) => {
    changeAreaToolValues(info);
  };
  const onContinousDrawArea = (info: any) => {
    changeAreaToolValues(info);
  };
  const areaOptions = {
    finishedcbf: onFinishedArea,
    resetcbf: () => changeAreaToolValues(undefined),
    continousdrawcbf: onContinousDrawArea,
    maxdist: 30,
  };

  //save measure coordinates values on finished
  const displayPosition = (info: any) => {
    changeCoordinateToolValues(info);
  };
  const coordinateOptions = {
    finishedcbf: displayPosition,
    errorcbf: error,
    resetcbf: () => changeCoordinateToolValues(undefined),
    continousdraw: true,
    finishinput: mm.USERINPUTTYPE["LEFTCLICK"],
  };

  //save measure profile values on finished
  const profileFinished = (
    pointArray: { x: number; y: number; z: number }[]
  ) => {
    pointArray = pointArray.map((point) => {
      return {
        x: Math.round(point.x * 1000) / 1000,
        y: Math.round(point.y * 1000) / 1000,
        z: Math.round(point.z * 1000) / 1000,
      };
    });
    changeProfileToolValues(pointArray);
  };
  const profileOptions = {
    profilefinishedcbf: profileFinished,
    errorcbf: error,
    resetcbf: resetProfile,
    continousdraw: true,
    maxdist: 30,
  };

  const orthogonalOptions = {
    continousdrawcbf: (info: any) => changeOrthogonalDistanceToolValues(info),
    resetcbf: () => changeOrthogonalDistanceToolValues(undefined),
    finishedcbf: (info: any) => changeOrthogonalDistanceToolValues(info),
  };

  const linrefOptions = {
    maxdist: 50,
    resetcbf: () => changeLinrefToolValues(null),
    finishedcbf: (info: any) => changeLinrefToolValues(info?.uvwCoords),
    continousdrawcbf: (info: any) => changeLinrefToolValues(info), // TODO: performance issue with this
    errorcbf: (msg: string) => {
      console.error(msg);
    },
  };

  //create options for all measurements tools
  useEffect(() => {
    if (viewer === undefined) return;
    setCoordinateTool(
      viewer
        .getScene()
        .getToolHandler()
        .createDrawPointTool(coordinateOptions, "pointMeasure", "measure")
    );
    changeDistanceTool(
      viewer
        .getScene()
        .getToolHandler()
        .createDrawLineStringTool(distOptions, "lengthMeasure", "measure")
    );
    changeSurveyWheelTool(
      viewer
        .getScene()
        .getToolHandler()
        .createSurveyWheelMeasureTool(
          surveyWheelOptions,
          "surveyWheel",
          "measure"
        )
    );
    setAreaTool(
      viewer
        .getScene()
        .getToolHandler()
        .createDrawPolygonTool(areaOptions, "areaMeasure", "measure")
    );
    setVerticalDistanceTool(
      viewer
        .getScene()
        .getToolHandler()
        .createElevationTool(verticalDistOptions, "verticalDistance", "measure")
    );
    changeProfileTool(
      viewer
        .getScene()
        .getToolHandler()
        .createProfileTool(profileOptions, "profile", "measure")
    );
    changeOrthogonalDistanceTool(
      viewer
        .getScene()
        .getToolHandler()
        .createOrthogonalMeasurementTool(
          orthogonalOptions,
          "orthogonalDistance",
          "measure"
        )
    );
    changeLinrefTool(
      viewer
        .getScene()
        .getToolHandler()
        .createDrawLRPointTool(linrefOptions, "linRefMeasure", "measure")
    );
    if (manager && clientConfig) manager.setViewer(viewer, clientConfig);
  }, [viewer]);

  const enableOrDisableTools = (toolsList: MeasurementType[]) => {
    if (viewer !== undefined) {
      //----------- distance tool --------------------------------------
      const distanceToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "distance"
      );
      if (distanceToolActivated !== undefined) {
        if (distanceTool && !distanceTool.enabled) distanceTool.enable();
      } else {
        if (distanceTool) distanceTool.disable();
        changeDistanceToolValues(undefined);
      }
      //----------- survey wheel tool --------------------------------------
      const surveyWheelToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "surveyWheel"
      );
      if (surveyWheelToolActivated !== undefined) {
        if (surveyWheelTool && !surveyWheelTool.enabled)
          surveyWheelTool.enable();
      } else {
        if (surveyWheelTool) surveyWheelTool.disable();
        changeDistanceToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- vertical distance ----------------------------------
      const verticalDistanceToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "verticalDistance"
      );
      if (
        verticalDistanceToolActivated !== undefined &&
        !(activeTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (verticalDistanceTool && !verticalDistanceTool.enabled)
          verticalDistanceTool.enable();
      } else {
        if (verticalDistanceTool) verticalDistanceTool.disable();
        changeVerticalDistanceToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- area measurement ----------------------------------
      const areaToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "measureArea"
      );
      if (areaToolActivated !== undefined) {
        if (areaTool && !areaTool.enabled) areaTool.enable();
      } else {
        if (areaTool) areaTool.disable();
        changeAreaToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- coordinate position ----------------------------------
      const coordinateToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "coordinate"
      );
      if (coordinateToolActivated !== undefined) {
        if (coordinateTool && !coordinateTool.enabled) coordinateTool.enable();
      } else {
        if (coordinateTool) coordinateTool.disable();
        changeCoordinateToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- profile datas ----------------------------------
      const profileToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "profile"
      );
      if (
        profileToolActivated !== undefined &&
        !(activeTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (profileTool && !profileTool.enabled) profileTool.enable();
      } else {
        if (profileTool) profileTool.disable();
        changeProfileToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- orthogonal distance ----------------------------------
      const orthogonalDistanceToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "orthogonalDistance"
      );
      if (
        orthogonalDistanceToolActivated !== undefined &&
        !(activeTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (orthogonalDistanceTool && !orthogonalDistanceTool.enabled)
          orthogonalDistanceTool.enable();
      } else {
        if (orthogonalDistanceTool) orthogonalDistanceTool.disable();
        changeOrthogonalDistanceToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- linref measure ----------------------------------

      const linrefToolActivated = toolsList.find(
        (tool: MeasurementType) => tool.id === "linref"
      );
      if (
        linrefToolActivated !== undefined &&
        !(activeTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (linrefTool && !linrefTool.enabled) linrefTool.enable();
      } else {
        if (linrefTool) linrefTool.disable();
        changeLinrefToolValues(null);
      }
      //----------------------------------------------------------------
    }
  };

  //shoq active tool after popover has been opened
  useEffect(() => {
    if (measurementPopover && lastMeasurementActiveTool.length > 0) {
      changeMeasurementActiveTool(lastMeasurementActiveTool);
    }
  }, [measurementPopover]);

  //check measure mode if can measure or not
  const checkMeasureMode = () => {
    const measureMode = viewer
      ?.getScene()
      .getMeasureModeHandler()
      .getMeasureMode();
    if (
      (measureMode === mm.MEASUREMODE.MULTIVIEW ||
        measureMode === mm.MEASUREMODE.PANODEPTHMAPINTERSECTION ||
        measureMode ===
          mm.MEASUREMODE.PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED ||
        measureMode === mm.MEASUREMODE.PANO_TWO_CLICK ||
        measureMode ===
          mm.MEASUREMODE.PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED) &&
      verticalDistanceTool &&
      orthogonalDistanceTool &&
      profileTool
    ) {
      setCanMeasure(true);
      verticalDistanceTool.disable();
      orthogonalDistanceTool.disable();
      profileTool.disable();
      surveyWheelTool.disable();
    } else {
      setCanMeasure(false);
    }
  };

  //check measure mode
  useEffect(() => {
    if (viewer) {
      checkMeasureMode();
    }
  }, [measurementPopover, activeCampaign, loading]);

  return (
    <Box height="100%" display={"flex"} flexDirection="column">
      <Box
        gap={0.5}
        paddingX={1.5}
        paddingY={1.5}
        sx={{
          height: "100%",
          overflow: "hidden",
          backgroundColor: darkTheme ? "darkblue.main" : "white",
          borderRadius: "10px",
          transition: "height 0.3s , margin 0.3s , padding 0.3s",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          display={"flex"}
          flexDirection="row"
          gap={1}
          alignItems="center"
          sx={{ color: darkTheme ? "white" : "darkblue.main" }}
        >
          <Typography
            fontWeight={"600"}
            noWrap
            marginRight={"auto"}
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            {t("measurement.title")}
          </Typography>
          {canShowSmallWindow && (
            <IconButton
              sx={{
                padding: 0,
                marginY: 1,
                color: darkTheme ? "white" : "darkblue.main",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
              }}
              onClick={() =>
                monitor(
                  apm,
                  "arrow-small-window | Measure",
                  handleToolSmallWindow
                )
              }
              disableRipple={true}
            >
              <ChevronLeft />
            </IconButton>
          )}
          <IconButton
            sx={{
              padding: 0,
              marginY: 1,
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
            onClick={() =>
              monitor(
                apm,
                "close-measure-tool | Measure",
                handleCloseMeasurement
              )
            }
            disableRipple={true}
          >
            <Close />
          </IconButton>
        </Box>
        <Box
          width="100%"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            marginBottom: 1,
            backgroundColor: darkTheme ? "primary.light" : "grey.light",
          }}
        >
          {tools.map((item) => {
            if (
              item.id === "linref" &&
              (!userData?.scope?.includes("addon/linref") ||
                axisCollection.length === 0)
            ) {
              return null;
            }
            return (
              <Tooltip
                title={item.tooltip}
                key={item.id}
                placement="bottom"
                enterDelay={400}
                classes={{
                  tooltip: "infra3d-buttonDescription",
                }}
              >
                <Button
                  disableRipple
                  key={item.id}
                  id={item.id}
                  onClick={() =>
                    monitor(
                      apm,
                      "activate-measure-tool | Measure",
                      () => activateTool(item),
                      { measureTool: item.id }
                    )
                  }
                  sx={{
                    width: "25%",
                    textAlign: "center",
                    borderRadius: "0px",
                    color: darkTheme ? "white" : "darkblue.main",
                    backgroundColor: measurementActiveTool.find(
                      (tool: MeasurementType) => tool.id === item.id
                    )
                      ? darkTheme
                        ? "darkblue.main"
                        : "white"
                      : "",
                  }}
                >
                  {measurementActiveTool.find(
                    (tool: MeasurementType) => tool.id === item.id
                  )
                    ? item.activeIcon
                    : item.icon}
                  {measurementActiveTool.find(
                    (tool: MeasurementType) => tool.id === item.id
                  ) && (
                    <Divider
                      sx={{
                        position: "absolute",
                        bottom: "0",
                        left: "50%",
                        backgroundColor: "secondary.main",
                        borderRadius: "4px 4px 0px 0px",
                        height: "2px",
                        marginLeft: "-12px",
                        width: "24px",
                      }}
                    />
                  )}
                </Button>
              </Tooltip>
            );
          })}
        </Box>
        <Box
          marginTop={maximizeTable ? "-128px" : "0px"}
          zIndex={1}
          width="100%"
          gap={1}
          sx={{
            backgroundColor: darkTheme ? "darkblue.main" : "white",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "auto",
            transition: "margin 0.3s",
            position: "relative",
          }}
        >
          {activeTool !== undefined ? (
            <>
              <Box height="auto" width={"100%"} position={"relative"}>
                <Box
                  display={"flex"}
                  width={"100%"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography
                    fontWeight={"700"}
                    noWrap
                    sx={{
                      color: darkTheme ? "white" : "darkblue.main",
                    }}
                  >
                    {activeTool.title}
                  </Typography>
                  {maximizeTable && (
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <IconButton
                        className="zoominout-button"
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={() =>
                          monitor(
                            apm,
                            "minimize-profile-tool | Measure",
                            () => {
                              changeShowDescription(false);
                              changeMaximizeTable(false);
                            }
                          )
                        }
                        sx={{
                          padding: 0,
                          color: darkTheme ? "white" : "darkblue.main",
                          "@media (hover: hover)": {
                            ":hover": { color: "secondary.main" },
                          },
                        }}
                      >
                        <ZoomInMap />
                      </IconButton>
                      <IconButton
                        sx={{
                          padding: 0,
                          marginY: 1,
                          color: darkTheme ? "white" : "darkblue.main",
                          "@media (hover: hover)": {
                            ":hover": { color: "secondary.main" },
                          },
                        }}
                        onClick={() =>
                          monitor(
                            apm,
                            "close-measure-tool | Measure",
                            handleCloseMeasurement
                          )
                        }
                        disableRipple={true}
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  )}
                  {!maximizeTable && (
                    <IconButton
                      onClick={() =>
                        monitor(
                          apm,
                          "change-status-description | Measure",
                          handleDescription
                        )
                      }
                      disableRipple
                      size="small"
                      sx={{
                        rotate: showDescription ? "-180deg" : "0deg",
                        transition: "rotate 0.1s",
                        padding: 0,
                        color: darkTheme ? "white" : "darkblue.main",
                        "@media (hover: hover)": {
                          ":hover": { color: "secondary.main" },
                        },
                      }}
                    >
                      <ExpandMore />
                    </IconButton>
                  )}
                </Box>
                <Box overflow={"hidden"}>
                  <Typography
                    fontWeight={"400"}
                    variant="body2"
                    sx={{
                      marginTop: showDescription ? "0%" : "-100%",
                      color: darkTheme ? "white" : "darkblue.main",
                      transition: "margin ease-in-out 0.2s",
                    }}
                    dangerouslySetInnerHTML={{ __html: activeTool.description }}
                  ></Typography>
                </Box>
              </Box>
              {canMeasure && activeTool.isBlockedOnOldView ? (
                <Box width={"100%"} overflow={"auto"} height={"inherit"}>
                  <Typography color={darkTheme ? "white" : "darkblue.main"}>
                    {t("measurement.blocked_tool_message")}
                  </Typography>
                </Box>
              ) : (
                <Box width={"100%"} overflow={"auto"} height={"inherit"}>
                  {activeTool.content}
                </Box>
              )}
            </>
          ) : (
            <Box
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              justifyContent={"center"}
              width={"100%"}
            >
              <img
                src={measurement_empty_state}
                width="280px"
                alt="Designed by pch.vector / Freepik"
              />
              <Typography
                variant="body2"
                textAlign={"center"}
                color={darkTheme ? "white" : "darkblue.main"}
              >
                {t("measurement.empty_state_message")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
